/**
 * ScenarioTour.jsx (iter227) — Fixed bottom-right panel that walks a
 * competition judge through the current Stockaris page in the context of
 * a specific demo scenario. Auto-appears when ``?scenario=<key>`` is on
 * the URL. Dismissable + step-by-step Next/Back/Finish buttons.
 *
 * Scenario tour scripts are keyed off the ``scenario`` query param so
 * a single tour can travel across screens (e.g. picking → packing) if
 * needed later.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { X, ArrowRight, ArrowLeft, Trophy, Lightbulb, CheckCircle } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import {
  completeCompetitionObjective, trackCompetitionEvent,
} from '../lib/api';

// ---------------------------------------------------------------------------
// Scripts — one entry per scenario key.
// ---------------------------------------------------------------------------
const TOURS = {
  picking_flow: {
    title: 'Scenario · Pick the urgent order',
    objective_key: 'process_urgent_order',
    accent: 'amber',
    steps: [
      { heading: 'Filter to the urgent orders',
        body: 'This is the real Orders screen. The seeded demo tenant has five orders flagged priority=urgent. Filter or scroll to find them.' },
      { heading: 'Open the order + start picking',
        body: 'Click an urgent order → Start Pick. In Stockaris this drives the warehouse-floor picker via location + SKU scans, not the office admin.' },
      { heading: 'Pack + dispatch',
        body: 'After the pick, move the order into pack + ship. The warehouse team drives every step; the office only sees the confirmation.' },
      { heading: 'Why this matters',
        body: 'Most WMS keep picking decisions in the office. Stockaris hands the scan, allocation, and confirmation to the person moving the stock — fewer errors, faster dispatch, less waiting on admin.' },
    ],
  },
  master_planner_whatif: {
    title: 'Scenario · Master Planner What-If',
    objective_key: null,   // dashboard scenario — no page walkthrough
    accent: 'sky',
    steps: [
      { heading: 'Fictional +35% demand surge',
        body: 'Imagine Major Retailer walks in with an order 35% bigger than a typical month across your best-moving SKUs.' },
      { heading: 'Projected shortage + PO suggestion',
        body: 'Stockaris projects the additional demand against current inventory, ranks the SKUs, and suggests a purchase-order size (with a 20% safety buffer).' },
      { heading: 'Why this matters',
        body: 'You can test the warehouse impact BEFORE committing to the customer — no more "yes" then scramble.' },
    ],
  },
  abc_layout: {
    title: 'Scenario · ABC Warehouse Layout',
    objective_key: null,
    accent: 'emerald',
    steps: [
      { heading: 'ABC overlay is now on',
        body: 'Red tiles = A (top 20% of movement), amber = B (next 30%), blue = C (slow-movers).' },
      { heading: 'Look for the bad neighbours',
        body: 'Red tiles far from the dispatch dock, or C tiles blocking prime pick paths — those are the ones costing you seconds every pick.' },
      { heading: 'Try a scenario',
        body: 'The Layout Designer lets you draft alternate layouts and run pick-path simulations. Nothing you do here touches production.' },
      { heading: 'Why this matters',
        body: "A warehouse shouldn't be rearranged just because a layout looks prettier. Stockaris measures how movement affects operational efficiency." },
    ],
  },
  warehouse_transfer: {
    title: 'Scenario · Paperwork vs the floor',
    objective_key: null,
    accent: 'sky',
    steps: [
      { heading: 'Look for TR-INBOUND-HERO',
        body: 'A multi-line inbound transfer from Melbourne DC is sitting on the dock. The paperwork arrived hours ago.' },
      { heading: "The office says 'received' — the warehouse says 'wait'",
        body: 'In most WMS the transfer is marked received when the admin ticks a box. Pickers then hunt for pallets that never landed.' },
      { heading: 'Stockaris drives status from the RF check-off',
        body: 'Open the transfer → RF scan each line on the dock → only then does stock become available for picking.' },
      { heading: 'Why this matters',
        body: 'No ghost stock. No pickers walking to empty locations. Stock available means physically present, not on paper.' },
    ],
  },
  pallet_tracking: {
    title: 'Scenario · Where are your pallets going?',
    objective_key: null,
    accent: 'emerald',
    steps: [
      { heading: 'Statement variance',
        body: 'The CHEP pooler says +60 pallets more than your ledger. That is a chargeback if you cannot explain it.' },
      { heading: 'Drill into the last 14 days of outbound',
        body: 'Group by merchant, kind, and reference. Damage + transfer_out are the usual suspects.' },
      { heading: 'Book the correcting movement in Stockaris',
        body: 'One tap — every future statement stays reconciled automatically.' },
      { heading: 'Why this matters',
        body: 'Fewer pooler charges. Faster monthly reconciliations. No more spreadsheet Friday.' },
    ],
  },
  offline_simulation: {
    title: 'Scenario · Internet failure',
    objective_key: null,
    accent: 'sky',
    steps: [
      { heading: 'Pull the plug',
        body: 'Click SIMULATE INTERNET FAILURE — the banner turns red and every warehouse action starts queuing locally instead of hitting the network.' },
      { heading: 'Keep the floor moving',
        body: 'Tap PICK, RECEIVE, COUNT and MOVE. Watch the queue counter tick up. Nothing waits for the internet to come back.' },
      { heading: 'Restore the connection',
        body: 'Click RESTORE CONNECTION. Stockaris shows "Synchronising warehouse activity…" then reports the total transactions replayed.' },
      { heading: 'Why this matters',
        body: 'Warehouses lose hours whenever the Wi-Fi drops. Stockaris keeps the floor productive during outages and syncs cleanly when the network returns.' },
    ],
  },
};

const ACCENT_CLASSES = {
  amber: { border: 'border-amber-500', ring: 'ring-amber-200', pill: 'bg-amber-500', hover: 'hover:bg-amber-600' },
  sky:   { border: 'border-sky-500',   ring: 'ring-sky-200',   pill: 'bg-sky-500',   hover: 'hover:bg-sky-600' },
  emerald: { border: 'border-emerald-500', ring: 'ring-emerald-200', pill: 'bg-emerald-500', hover: 'hover:bg-emerald-600' },
};

export default function ScenarioTour() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const scenarioKey = useMemo(() => {
    const p = new URLSearchParams(location.search);
    return p.get('scenario');
  }, [location.search]);
  const tour = scenarioKey ? TOURS[scenarioKey] : null;

  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(true);
  const [completed, setCompleted] = useState(false);

  useEffect(() => { setStep(0); setVisible(true); setCompleted(false); }, [scenarioKey]);

  useEffect(() => {
    if (tour && scenarioKey) {
      trackCompetitionEvent('demo.scenario_tour_opened', scenarioKey).catch(() => {});
    }
  }, [tour, scenarioKey]);

  if (!user?.company?.is_demo || !tour || !visible) return null;

  const accent = ACCENT_CLASSES[tour.accent] || ACCENT_CLASSES.emerald;
  const s = tour.steps[step];
  const last = step >= tour.steps.length - 1;

  const finish = async () => {
    setCompleted(true);
    trackCompetitionEvent('demo.scenario_tour_finished', scenarioKey).catch(() => {});
    if (tour.objective_key) {
      try { await completeCompetitionObjective(tour.objective_key); } catch { /* noop */ }
    }
  };

  return (
    <div
      className={`fixed bottom-24 right-4 w-[360px] max-w-[calc(100vw-2rem)] z-[60] bg-white shadow-2xl border-l-4 ${accent.border} ring-4 ${accent.ring} p-4`}
      data-testid={`scenario-tour-${scenarioKey}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-neutral-500">
          <Trophy size={12} weight="fill" /> {tour.title}
        </div>
        <button
          onClick={() => setVisible(false)}
          className="text-neutral-400 hover:text-neutral-800"
          data-testid={`scenario-tour-close-${scenarioKey}`}
        >
          <X size={14} />
        </button>
      </div>

      {!completed ? (
        <>
          <div className="text-xs text-neutral-500 mb-2">
            Step {step + 1} of {tour.steps.length}
          </div>
          <h3 className="text-base font-black leading-tight mb-1" data-testid={`scenario-tour-heading-${scenarioKey}`}>
            {s.heading}
          </h3>
          <p className="text-sm text-neutral-700 leading-snug mb-4">
            {s.body}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm"
              disabled={step === 0}
              onClick={() => setStep((v) => v - 1)}
              className="text-xs uppercase tracking-widest"
              data-testid={`scenario-tour-back-${scenarioKey}`}
            >
              <ArrowLeft size={12} className="mr-1" /> Back
            </Button>
            {last ? (
              <Button size="sm" onClick={finish}
                className={`ml-auto rounded-none ${accent.pill} ${accent.hover} text-white uppercase tracking-widest text-xs font-black`}
                data-testid={`scenario-tour-finish-${scenarioKey}`}
              >
                <Lightbulb size={12} className="mr-1" /> Finish
              </Button>
            ) : (
              <Button size="sm" onClick={() => setStep((v) => v + 1)}
                className={`ml-auto rounded-none ${accent.pill} ${accent.hover} text-white uppercase tracking-widest text-xs font-black`}
                data-testid={`scenario-tour-next-${scenarioKey}`}
              >
                Next <ArrowRight size={12} className="ml-1" />
              </Button>
            )}
          </div>
        </>
      ) : (
        <div className="space-y-3" data-testid={`scenario-tour-done-${scenarioKey}`}>
          <div className="flex items-center gap-2 text-emerald-600 text-sm font-black uppercase tracking-widest">
            <CheckCircle size={16} weight="fill" /> Scenario complete
          </div>
          <p className="text-xs text-neutral-600">
            You can keep exploring this screen, or head back to the challenge dashboard.
          </p>
          <Button
            size="sm"
            onClick={() => navigate('/competition-demo/dashboard')}
            className="w-full rounded-none bg-neutral-900 hover:bg-neutral-800 text-white uppercase tracking-widest text-xs font-black"
            data-testid={`scenario-tour-return-${scenarioKey}`}
          >
            Return to challenge
          </Button>
        </div>
      )}
    </div>
  );
}
